import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  BookOpen, Brain,
  Calendar,
  CheckCircle,
  Cpu,
  Edit,
  FileText,
  GraduationCap,
  MessageCircle,
  PlayCircle,
  Shield,
  Sparkles, TrendingUp,
  Users,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from './HomePageLab';

import PESASimulatorSpot from './PESASimulatorSpot';

const STATS_SOURCE = 'Cohortes pilotes · 2011–2025';

const PROBLEMES = [
  { n: '01', titre: 'Cours magistraux = décrochage', desc: "Taux d'attention sous les 40% après 20 minutes. Les concepts arrivent — ils ne restent pas.", ref: 'PROB-ATT' },
  { n: '02', titre: 'Bourdieu reste abstrait', desc: "Mémorisé pour l'examen, oublié en trois semaines. Le concept n'a jamais été vécu.", ref: 'PROB-RET' },
  { n: '03', titre: 'LMS = silos de PDF', desc: "Des entrepôts riches de contenus sans feedback adaptatif ni trace réflexive.", ref: 'PROB-LMS' },
];

const SOLUTIONS = [
  { icon: <Brain size={32} />, titre: 'Simulations causales', desc: "Case 'Violence Symbolique' : 240 min où l'étudiant navigue dans un système éducatif et voit les mécanismes de Bourdieu en action — la théorie en pratique.", detail: '7 étapes · 4 trajectoires possibles', ref: 'SIM' },
  { icon: <Sparkles size={32} />, titre: 'Contrefactuels dynamiques', desc: '"Si vous aviez choisi B, votre compréhension serait passée de 67% à 82%." Feedback adaptatif impossible avec les LMS classiques.', detail: 'Calibration algorithmique · Temps réel', ref: 'CTF' },
  { icon: <TrendingUp size={32} />, titre: 'Analytics enseignant', desc: "Dashboard temps réel : heatmaps d'engagement, trajectoires d'apprentissage, identification des points de blocage collectifs.", detail: 'Visible dès J+1 de déploiement', ref: 'ANL' },
  { icon: <BookOpen size={32} />, titre: 'Parcours clefs en mains', desc: "'Éducation & Société' (570 min), 'Art Critique' (240 min), 'Conflits Internes' (180 min) — sur-mesures, adaptables.", detail: 'Références historiques intégrées', ref: 'PCK' },
];

const SECTIONS = [
  { id: 'dashboard',  short: 'SYS',  color: 'orange',  icon: Cpu         },
  { id: 'parcours',   short: 'CONT', color: 'orange',  icon: FileText    },
  { id: 'eleves',     short: 'APP',  color: 'orange',  icon: Users       },
  { id: 'cohortes',   short: 'EMP',  color: 'emerald', icon: Calendar    },
  { id: 'personnel',  short: 'OPS',  color: 'sky',     icon: Shield      },
  { id: 'branding',   short: 'ID',   color: 'purple',  icon: Edit        },
  { id: 'stats',      short: 'TLM',  color: 'indigo',    icon: BarChart3   },
  { id: 'contact',    short: 'MESS', color: 'slate',   icon: MessageCircle },
  { id: 'modération', short: 'SEC',  color: 'rose',    icon: AlertTriangle },
];

export function GrandeEcoleContPreview() {
  return (
    <div className="relative w-full h-full bg-zinc-950 overflow-hidden flex flex-col font-mono">
      {/* Grille de fond */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(#52525b 1px, transparent 1px), linear-gradient(90deg, #52525b 1px, transparent 1px)',
        backgroundSize: '32px 32px'
      }} />
      <div className="absolute top-1/4 left-1/3 w-72 h-72 bg-orange-500/5 rounded-full blur-[80px] pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-60 h-60 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 h-8 sm:h-10 border-b border-orange-900/30 bg-black/70 flex items-center px-2 sm:px-4 gap-2 sm:gap-3 shrink-0">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" />
        <span className="font-black text-orange-300 text-[10px] sm:text-xs tracking-tighter truncate">
          GRANDE ÉCOLE <span className="text-orange-500/60">CONTRÔLE</span>
        </span>
        <div className="hidden sm:block h-3 w-px bg-orange-900/50 mx-1 shrink-0" />
        <span className="hidden sm:inline text-[11px] text-emerald-400/80 uppercase tracking-wider truncate">
          CONT — CRÉATEUR ACTIF
        </span>
        <div className="ml-auto text-[9px] sm:text-[10px] text-zinc-600 uppercase tracking-widest shrink-0">
          v1.0
        </div>
      </header>

      <div className="relative z-10 flex flex-1 overflow-hidden min-h-0">
        {/* Sidebar */}
        <nav className="w-10 sm:w-14 border-r border-zinc-800/60 bg-black/30 flex flex-col items-center pt-2 sm:pt-3 gap-0.5 shrink-0">
          {SECTIONS.map(s => {
            const active = s.id === 'parcours';
            return (
              <div key={s.id}
                className={`w-full flex flex-col items-center py-1.5 sm:py-2 gap-0.5 transition-all relative ${
                  active ? 'bg-orange-950/40 text-orange-300' : 'text-zinc-600'
                }`}>
                {active && <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-orange-500" />}
                <s.icon size={12} className={active ? 'text-orange-400' : 'text-zinc-600'} />
                <span className="text-[6px] sm:text-[7px] uppercase tracking-tighter">{s.short}</span>
              </div>
            );
          })}
        </nav>

        <main className="flex-1 flex flex-col p-2 sm:p-4 overflow-hidden gap-2 sm:gap-3 min-w-0">
          {/* Breadcrumb */}
          <div className="flex items-baseline gap-2 pb-1.5 sm:pb-2 border-b border-orange-900/20 shrink-0 min-w-0">
            <span className="text-[10px] sm:text-xs font-black text-orange-300/80 uppercase tracking-tight truncate">
              CHAÎNE CONTENUS
            </span>
            <code className="hidden sm:inline text-[10px] text-zinc-600 bg-black/40 px-1.5 py-0.5 border border-zinc-800 shrink-0">
              /parcours
            </code>
            <span className="ml-auto hidden md:inline text-[10px] text-zinc-600 italic truncate">
              Noyau de simulation · Compilation protocoles
            </span>
          </div>

          {/* Cartes */}
          <div className="flex-1 grid grid-cols-2 gap-2 sm:gap-3 min-h-0">
            <CreatorCard
              accent="orange"
              title="Parcours Creator"
              description="Séquences pédagogiques, certifications internes, trajectoires collectives."
              stats={[['5', 'Étapes'], ['Cross', 'Valid.'], ['/parcours', 'Sortie']]}
              delay={0.15}
            />
            <CreatorCard
              accent="indigo"
              title="Case Creator"
              description="Simulations avancées — graphes causaux, contrefactuels, calibration."
              stats={[['5', 'Étapes'], ['Bayesian', 'Moteur'], ['/sim.', 'Sortie']]}
              delay={0.3}
            />
          </div>

          {/* Footer */}
          <div className="flex flex-wrap items-center gap-x-2.5 sm:gap-x-4 gap-y-0.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-900/40 border border-zinc-800/40 rounded-lg text-[9px] sm:text-[10px] shrink-0">
            <StatusItem dotClass="bg-emerald-400" label="Moteur" val="OK" valClass="text-emerald-400" />
            <StatusItem label="Protocoles" val="2 actifs" valClass="text-orange-400" />
            <StatusItem label="File" val="0" valClass="text-amber-400" />
            <StatusItem label="Simulation" val="Prêt" valClass="text-indigo-400" />
          </div>
        </main>
      </div>
    </div>
  );
}

function CreatorCard({ accent, title, description, stats, delay }) {
  const isOrange = accent === 'orange';
  const titleColor = isOrange ? 'text-orange-300' : 'text-indigo-300';
  const dotColor   = isOrange ? 'bg-orange-400' : 'bg-indigo-400';
  const valColor   = isOrange ? 'text-orange-400' : 'text-indigo-400';
  const hoverBg    = isOrange ? 'hover:bg-orange-950/30 hover:border-orange-600/40'
                              : 'hover:bg-indigo-950/30 hover:border-indigo-600/40';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="relative flex flex-col bg-gradient-to-br from-zinc-900/90 to-zinc-950 border border-zinc-800 rounded-xl p-2.5 sm:p-4 overflow-hidden min-w-0"
    >
      <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3 min-w-0">
        <div className={`w-1.5 h-1.5 ${dotColor} rounded-full animate-pulse shrink-0`} />
        <span className={`text-[10px] sm:text-[11px] font-medium ${titleColor} tracking-wide uppercase truncate`}>
          {title}
        </span>
      </div>

      {/* Description : cachée UNIQUEMENT sous sm (mobile), visible en desktop */}
      <p className="hidden sm:block text-[10px] text-zinc-400 font-light leading-relaxed mb-3 flex-1">
        {description}
      </p>

      {/* Stats : 2 sur mobile, 3 sur desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2 pt-2 border-t border-zinc-800/50 mb-2 sm:mb-3">
        {stats.map(([val, label], i) => (
          <div
            key={i}
            className={`min-w-0 ${i === 2 ? 'hidden sm:block' : ''}`}
          >
            <div className={`text-[9px] sm:text-[10px] font-light ${valColor} truncate`}>{val}</div>
            <div className="text-[9px] sm:text-[10px] text-zinc-600 truncate">{label}</div>
          </div>
        ))}
      </div>

      <div className={`flex items-center justify-between px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-800/40 ${hoverBg} border border-zinc-700/40 rounded-lg transition-all cursor-default gap-2 min-w-0`}>
        <span className="text-[10px] sm:text-[11px] text-zinc-400 truncate">Lancer le Créateur</span>
        <span className="text-zinc-600 text-[10px] shrink-0">→</span>
      </div>
    </motion.div>
  );
}

function StatusItem({ dotClass, label, val, valClass }) {
  return (
    <div className="flex items-center gap-1 min-w-0">
      {dotClass && <div className={`w-1 h-1 sm:w-1.5 sm:h-1.5 ${dotClass} rounded-full animate-pulse shrink-0`} />}
      <span className="text-zinc-600 truncate">{label}</span>
      <span className={`font-medium ${valClass} truncate`}>{val}</span>
    </div>
  );
}

// ── Ligne de coordonnées — décoration concept "carte" ──
const CoordLine = ({ x1, y1, x2, y2, delay = 0 }) => (
  <motion.line
    x1={x1} y1={y1} x2={x2} y2={y2}
    stroke="rgba(34,211,238,0.12)"
    strokeWidth="0.5"
    initial={{ pathLength: 0, opacity: 0 }}
    animate={{ pathLength: 1, opacity: 1 }}
    transition={{ duration: 1.8, delay, ease: 'easeOut' }}
  />
);

const CoordMarker = ({ x, y, label, delay = 0 }) => (
  <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay, duration: 0.6 }}>
    <circle cx={x} cy={y} r="2.5" fill="rgba(34,211,238,0.4)" />
    <circle cx={x} cy={y} r="6" fill="none" stroke="rgba(34,211,238,0.15)" strokeWidth="0.5" />
    <text x={x + 10} y={y + 4} fontSize="7" fill="rgba(34,211,238,0.5)"
      fontFamily="monospace" letterSpacing="0.1em">{label}</text>
  </motion.g>
);

const CartePedagogique = () => (
  <div className="relative w-full h-full">
    <div className="absolute inset-0 opacity-[0.06]" style={{
      backgroundImage: `
        linear-gradient(rgba(34,211,238,0.8) 1px, transparent 1px),
        linear-gradient(90deg, rgba(34,211,238,0.8) 1px, transparent 1px),
        linear-gradient(rgba(34,211,238,0.3) 1px, transparent 1px),
        linear-gradient(90deg, rgba(34,211,238,0.3) 1px, transparent 1px)
      `,
      backgroundSize: '80px 80px, 80px 80px, 16px 16px, 16px 16px',
    }} />

    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 500 420" preserveAspectRatio="xMidYMid meet">
      <CoordLine x1="60" y1="80"  x2="440" y2="80"  delay={0.2} />
      <CoordLine x1="60" y1="200" x2="440" y2="200" delay={0.5} />
      <CoordLine x1="60" y1="340" x2="440" y2="340" delay={0.8} />
      <CoordLine x1="60" y1="60"  x2="60"  y2="360" delay={0.3} />
      <CoordLine x1="250" y1="60" x2="250" y2="360" delay={0.6} />
      <CoordLine x1="440" y1="60" x2="440" y2="360" delay={0.9} />

      <motion.path
        d="M 80 320 C 140 280, 160 180, 200 160 C 240 140, 260 120, 320 100 C 360 88, 400 82, 430 78"
        fill="none"
        stroke="rgba(34,211,238,0.5)"
        strokeWidth="1.5"
        strokeDasharray="4 3"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 2.5, delay: 1.0, ease: 'easeOut' }}
      />

      <motion.path
        d="M 80 320 C 130 300, 150 260, 180 240 C 220 215, 260 210, 310 200 C 360 190, 400 185, 430 180"
        fill="none"
        stroke="rgba(245,158,11,0.35)"
        strokeWidth="1"
        strokeDasharray="2 4"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 2.5, delay: 1.4, ease: 'easeOut' }}
      />

      <CoordMarker x={80}  y={320} label="DÉPART"  delay={1.2} />
      <CoordMarker x={200} y={160} label="J+30"    delay={1.6} />
      <CoordMarker x={320} y={100} label="J+90"    delay={2.0} />
      <CoordMarker x={430} y={78}  label="MAÎTRISE" delay={2.4} />

      <motion.rect
        x="100" y="250" width="100" height="60" rx="2"
        fill="rgba(239,68,68,0.04)"
        stroke="rgba(239,68,68,0.15)"
        strokeWidth="0.5"
        strokeDasharray="3 2"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.6 }}
      />
      <motion.text
        x="150" y="275" textAnchor="middle"
        fontSize="6" fill="rgba(239,68,68,0.5)"
        fontFamily="monospace" letterSpacing="0.1em"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 2.0 }}
      >ZONE DÉCROCHAGE</motion.text>
      <motion.text
        x="150" y="286" textAnchor="middle"
        fontSize="6" fill="rgba(239,68,68,0.4)"
        fontFamily="monospace"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 2.1 }}
      >Cours magistral standard</motion.text>

      <motion.g
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.6, duration: 0.5 }}
      >
        <rect x="370" y="58" width="70" height="18" rx="2"
          fill="rgba(34,211,238,0.08)" stroke="rgba(34,211,238,0.3)" strokeWidth="0.5" />
        <text x="405" y="70" textAnchor="middle"
          fontSize="7" fill="rgba(34,211,238,0.8)"
          fontFamily="monospace" fontWeight="bold" letterSpacing="0.15em">TRAJECTOIRE PESACORE</text>
      </motion.g>
    </svg>

    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 2.8, duration: 0.6 }}
      className="absolute bottom-4 right-4 flex flex-col gap-1.5"
    >
      <div className="flex items-center gap-2">
        <div className="w-6 h-px" style={{ background: 'rgba(34,211,238,0.5)', borderTop: '1.5px dashed rgba(34,211,238,0.5)' }} />
        <span className="text-[11px] font-mono text-indigo-400/60 uppercase tracking-wider">Trajectoire PESACORE</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-6 h-px" style={{ borderTop: '1px dashed rgba(245,158,11,0.35)' }} />
        <span className="text-[11px] font-mono text-amber-500/50 uppercase tracking-wider">Contrefactuel</span>
      </div>
    </motion.div>
  </div>
);

export default function LandingPageEducation() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [showSpot, setShowSpot] = useState(false);
  const heroRef = useRef(null);

  return (
    <div className="min-h-screen bg-[#020617] text-indigo-50 relative overflow-hidden" style={{ fontFamily: 'Georgia, serif' }}>

      {/* Fond : papier millimétré */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `
          linear-gradient(rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.03) 1px, transparent 1px),
          linear-gradient(rgba(34,211,238,0.015) 1px, transparent 1px),
          linear-gradient(90deg, rgba(34,211,238,0.015) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px, 80px 80px, 16px 16px, 16px 16px',
      }} />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_30%_20%,rgba(34,211,238,0.07),transparent_55%)]" />
      <div className="fixed inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_70%_80%,rgba(245,158,11,0.04),transparent_55%)]" />

      <Header />

      <section ref={heroRef} className="relative pt-24 sm:pt-28 pb-12 sm:pb-24 px-4 sm:px-6 min-h-[auto] sm:min-h-[90vh] flex items-start sm:items-center">
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">

          {/* Colonne gauche */}
          <div className="order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6 sm:mb-8"
            >
              <div className="h-px w-8 bg-indigo-200" />
              <span className="text-[11px] font-mono uppercase tracking-[0.25em] sm:tracking-[0.35em] text-indigo-200">
                PESACORE · Academia · Réf. EDU-2026
              </span>
              <div className="flex items-center gap-1.5 ml-auto">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[11px] font-mono text-emerald-400/60 uppercase tracking-widest">Actif</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 sm:mb-6"
              style={{
                border: '1px solid rgba(34,211,238,0.12)',
                background: 'rgba(34,211,238,0.03)',
              }}
            >
              <GraduationCap size={13} className="text-amber-400" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-indigo-300/70">
                Pour établissements &amp; enseignants
              </span>
            </motion.div>

            {/* MOBILE FIX : clamp() sur les deux lignes du H1 */}
            <div className="mb-5 sm:mb-6 overflow-hidden">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="font-black uppercase tracking-tight leading-[0.95] text-white"
                style={{ fontFamily: 'Georgia, serif', fontSize: 'clamp(2rem, 8vw, 3.75rem)' }}
              >
                Vos étudiants
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <span
                  className="font-black italic tracking-tight leading-[0.95]"
                  style={{
                    fontSize: 'clamp(2rem, 8vw, 3.75rem)',
                    background: 'linear-gradient(135deg, #22d3ee 0%, #f59e0b 60%, #22d3ee 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                  vivent les concepts.
                </span>
              </motion.div>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.7 }}
              className="text-indigo-100 leading-relaxed mb-2 max-w-lg"
              style={{ fontSize: 'clamp(0.82rem, 2.5vw, 1rem)' }}
            >
              PESACORE transforme vos cours en simulations causales immersives.
              Bourdieu, Freire, Bernstein ne sont pas simplement mémorisés.{' '}
              <span className="text-white font-semibold not-italic">Ils sont expérimentés.</span>
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0, duration: 0.7 }}
              className="text-xs font-mono text-slate-300 mb-6 sm:mb-8 leading-relaxed"
            >
              Méthode éprouvée depuis 2011 · Archive DREETS · 50% de remise en mouvement dès le premier projet<br />
              <a
                href="https://centre-val-de-loire.dreets.gouv.fr/sites/centre-val-de-loire.dreets.gouv.fr/IMG/pdf/BAT-LETTRE_FSE-LA_METHODE_pour_publi.pdf"
                target="_blank" rel="noopener noreferrer"
                className="text-indigo-500/60 hover:text-indigo-400/80 transition-colors"
              >
                Dépôt officiel / FSE (2011–2014) ↗
              </a>
            </motion.p>

            {/* CTAs — MOBILE FIX : flex-col par défaut, row sur sm+, boutons full-width mobile */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8"
            >
              <button
                onClick={() => navigate('/auth/register?plan=academia')}
                className="group relative flex items-center justify-center gap-3 px-7 py-3.5 overflow-hidden w-full sm:w-auto"
                style={{ background: 'rgba(245,158,11,0.12)', border: '1.5px solid rgba(245,158,11,0.4)' }}
              >
                <motion.span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(245,158,11,0.08), transparent)' }}
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                />
                <Sparkles size={15} className="text-amber-400 relative z-10" />
                <span className="text-sm font-black uppercase tracking-wider text-white relative z-10">Essai 30 jours gratuit</span>
                <ArrowRight size={15} className="text-amber-400 group-hover:translate-x-1 transition-transform relative z-10" />
              </button>

              <button
                onClick={() => navigate('/contact?from=education&sujet=grandeecole')}
                className="flex items-center justify-center gap-2 px-6 py-3.5 transition-all w-full sm:w-auto"
                style={{ border: '1px solid rgba(34,211,238,0.1)' }}
                onMouseEnter={e => e.currentTarget.style.border = '1px solid rgba(34,211,238,0.22)'}
                onMouseLeave={e => e.currentTarget.style.border = '1px solid rgba(34,211,238,0.1)'}
              >
                <PlayCircle size={16} className="text-indigo-400/70" />
                <span className="font-mono uppercase tracking-widest text-slate-400 text-xs">Voir la démo</span>
              </button>
            </motion.div>

            {/* Métriques — MOBILE FIX : flex-wrap + overflow-x-auto pour éviter débordement */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.6 }}
              className="flex items-center gap-4 sm:gap-6 overflow-x-auto pb-1"
            >
              {[
                { val: '78%', label: 'Engagement moyen' },
                { val: '+40%', label: 'Rétention concepts' },
                { val: '−35%', label: 'Tps formation' },
              ].map((m, i) => (
                <div key={i} className="flex flex-col shrink-0">
                  <span className="text-lg sm:text-xl font-black text-white tracking-tight leading-none">{m.val}</span>
                  <span className="text-[11px] font-mono text-indigo-500/50 uppercase tracking-wider mt-1 whitespace-nowrap">{m.label}</span>
                </div>
              ))}
              <div className="h-px flex-1 bg-gradient-to-r from-indigo-500/10 to-transparent hidden md:block" />
              <span className="text-[10px] font-mono text-slate-300 uppercase tracking-widest hidden md:block">{STATS_SOURCE}</span>
            </motion.div>
          </div>

          {/* Colonne droite : carte SVG
              MOBILE FIX : order-1 pour passer en premier sur mobile, hauteur réduite */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.0, delay: 0.4 }}
            className="relative order-1 lg:order-2 bg-[#020617]/80"
            style={{
              height: 'clamp(240px, 45vw, 420px)',
              border: '1px solid rgba(34,211,238,0.1)',
              boxShadow: '0 0 60px rgba(34,211,238,0.04) inset',
            }}
          >
            <span className="absolute top-0 left-0 w-5 h-5 border-t border-l border-indigo-500/30" />
            <span className="absolute top-0 right-0 w-5 h-5 border-t border-r border-indigo-500/30" />
            <span className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-amber-500/25" />
            <span className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-amber-500/25" />

            <CartePedagogique />

            <div className="absolute top-3 left-3 flex items-center gap-2">
              <span className="text-[10px] font-mono text-indigo-100 uppercase tracking-[0.25em] hidden sm:block">
                Trajectoire · Apprentissage · Simulation causale
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ SECTION PROBLÈMES ══ */}
      <section className="py-12 sm:py-20 px-4 sm:px-6" style={{ borderTop: '1px solid rgba(34,211,238,0.05)', borderBottom: '1px solid rgba(34,211,238,0.05)', background: 'rgba(2,6,23,0.6)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="text-[11px] font-mono text-indigo-200 uppercase tracking-[0.3em] mb-1">Section I</div>
            <div className="flex items-end gap-6">
              <h2 className="font-black uppercase tracking-tight text-white leading-tight"
                style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
                Le problème que vous{' '}
                <span style={{ background: 'linear-gradient(90deg, #f97316, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  connaissez trop bien
                </span>
              </h2>
              <div className="h-px flex-1 bg-gradient-to-r from-indigo-500/20 to-transparent hidden md:block mb-2" />
            </div>
          </div>

          {/* MOBILE FIX : grid-cols-1 → md:grid-cols-3, borderRight retiré sur mobile */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {PROBLEMES.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="p-5 sm:p-8 relative group transition-all"
                style={{
                  background: 'rgba(2,6,23,0.0)',
                  borderRight: i < 2 ? '1px solid rgba(34,211,238,0.07)' : 'none',
                  borderTop: '1px solid rgba(34,211,238,0.07)',
                }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'rgba(34,211,238,0.015)' }} />

                <div className="absolute top-4 right-4 text-[10px] font-mono text-indigo-500/20 uppercase tracking-widest hidden sm:block">{p.ref}</div>

                <div className="text-[11px] font-mono text-indigo-600/50 mb-4 tracking-widest">{p.n} ·</div>
                <h3 className="text-base font-black uppercase text-white mb-3 leading-tight tracking-tight relative z-10">{p.titre}</h3>
                <p className="text-slate-400 text-sm leading-relaxed relative z-10" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>{p.desc}</p>

                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-px"
                  style={{ background: 'rgba(34,211,238,0.3)' }}
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SECTION SOLUTIONS ══ */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-baseline gap-6 mb-8 sm:mb-12">
            <div>
              <div className="text-[11px] font-mono text-indigo-200 uppercase tracking-[0.3em] mb-1">Section II</div>
              {/* MOBILE FIX : clamp h2 */}
              <h2 className="font-black uppercase tracking-tight text-white"
                style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
                La solution{' '}
                <span style={{ background: 'linear-gradient(90deg, #22d3ee, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  PESACORE
                </span>
              </h2>
            </div>
          </div>

          {/* MOBILE FIX : grid-cols-1 → md:grid-cols-2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {SOLUTIONS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group relative p-5 sm:p-7 transition-all"
                style={{
                  background: 'rgba(2,6,23,0.8)',
                  borderTop: '1px solid rgba(34,211,238,0.06)',
                  borderRight: (i % 2 === 0) ? '1px solid rgba(34,211,238,0.06)' : 'none',
                }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                  style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.025), rgba(245,158,11,0.015))' }} />

                <div className="absolute top-4 right-5 text-[10px] font-mono text-indigo-500/15 uppercase tracking-widest hidden sm:block">{s.ref}</div>

                <div className="text-indigo-400/60 mb-4 group-hover:text-indigo-400/90 transition-colors relative z-10">{s.icon}</div>
                <h3 className="text-base sm:text-lg font-black uppercase tracking-tight text-white mb-2 relative z-10">{s.titre}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4 relative z-10" style={{ fontStyle: 'italic' }}>{s.desc}</p>
                <span className="text-[10px] font-mono text-indigo-500/50 uppercase tracking-wider relative z-10"
                  style={{ borderBottom: '1px solid rgba(34,211,238,0.15)', paddingBottom: '2px' }}>{s.detail}</span>
              </motion.div>
            ))}
            <div className="col-span-1 md:col-span-2" style={{ borderTop: '1px solid rgba(34,211,238,0.06)' }} />
          </div>
        </div>
      </section>

      {/* ══ PANEL GRANDE ÉCOLE ══ */}
      <section
        className="py-16 sm:py-20 px-5 sm:px-6"
        style={{
          borderTop: '1px solid rgba(34,211,238,0.05)',
          borderBottom: '1px solid rgba(34,211,238,0.05)',
          background: 'rgba(2,6,23,0.6)',
        }}
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14 items-center">
          {/* Texte — en premier sur mobile aussi pour la lecture */}
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="order-1"
          >
            <div className="text-[11px] font-mono text-amber-500/50 uppercase tracking-[0.3em] mb-3">
              Section III
            </div>

            <h2
              className="font-black uppercase tracking-tight mb-5 text-white leading-[1.05]"
              style={{ fontSize: 'clamp(1.65rem, 6vw, 2.25rem)' }}
            >
              Construisez vos propres{' '}
              <span
                className="italic font-normal block sm:inline"
                style={{
                  fontSize: 'clamp(1.3rem, 5vw, 1.875rem)',
                  background: 'linear-gradient(90deg, #f59e0b, #f97316)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                parcours et simulations
              </span>
            </h2>

            <p className="text-slate-100 leading-relaxed mb-4 text-sm sm:text-base italic">
              Le panel Grande École donne à vos administrateurs la capacité de créer,
              configurer et déployer des parcours complets — sans intervention technique.
            </p>

            <p className="text-slate-100 text-xs leading-relaxed mb-7 sm:mb-8 font-mono uppercase tracking-wider">
              Gestion multi-cohortes · Analytics par promotion · Attribution des rôles · Certifications
            </p>

            <button
              onClick={() => navigate('/contact?from=education&sujet=grandeecole')}
              className="flex items-center gap-2 px-6 py-3.5 text-white text-sm font-mono uppercase tracking-wider transition-all w-full sm:w-auto justify-center sm:justify-start"
              style={{
                border: '1px solid rgba(245,158,11,0.3)',
                background: 'rgba(245,158,11,0.06)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.border = '1px solid rgba(245,158,11,0.5)';
                e.currentTarget.style.background = 'rgba(245,158,11,0.10)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.border = '1px solid rgba(245,158,11,0.3)';
                e.currentTarget.style.background = 'rgba(245,158,11,0.06)';
              }}
            >
              Demander une démonstration <ArrowRight size={14} />
            </button>
          </motion.div>

          {/* Preview — plus haute et mieux proportionnée sur mobile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative order-2"
          >
            <div
              className="absolute -inset-3 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at 50% 50%, rgba(245,158,11,0.06), transparent 70%)',
              }}
            />

            <div
              className="relative bg-black overflow-hidden"
              style={{
                height: 'clamp(340px, 90vw, 420px)',
                border: '1px solid rgba(245,158,11,0.12)',
              }}
            >
              
              <div className="absolute inset-0 top-6">
                <GrandeEcoleContPreview />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ DÉMO SIMULATEUR ══ */}
      <section id="demo-video" className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <div className="text-[11px] font-mono text-indigo-200 uppercase tracking-[0.3em] mb-2">Section IV</div>
            {/* MOBILE FIX : clamp h2 */}
            <h2 className="font-black uppercase tracking-tight text-white mb-2"
              style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
              Le simulateur{' '}
              <span style={{ background: 'linear-gradient(90deg, #a78bfa, #22d3ee, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                en action
              </span>
            </h2>
            <p className="text-slate-100 font-mono text-xs uppercase tracking-wider">Simulation causale immersive · 40 secondes</p>
          </div>
          <div className="relative bg-black/60" style={{
              aspectRatio: '16/9',          // desktop
              border: '1px solid rgba(34,211,238,0.08)',
                      ...(window.innerWidth < 768 && window.innerWidth <= window.innerHeight
                ? { aspectRatio: 'unset', height: '85vh' }
                : window.innerWidth < 768
                  ? { aspectRatio: 'unset', height: '60vw', minHeight: '320px' }  // paysage mobile
                  : {}
              ),
              overflow: 'hidden',
            }}>
            <span className="absolute top-0 left-0 w-5 h-5 border-t border-l border-indigo-500/25" />
            <span className="absolute top-0 right-0 w-5 h-5 border-t border-r border-indigo-500/25" />
            <span className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-amber-500/20" />
            <span className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-amber-500/20" />

            {!showSpot ? (
              <div className="absolute inset-0 flex items-center justify-center cursor-pointer group" onClick={() => setShowSpot(true)}>
                <div className="text-center">
                  <motion.div
                    animate={{ scale: [1, 1.06, 1] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    {/* MOBILE FIX : taille icône réduite sur mobile */}
                    <PlayCircle size={48} className="sm:hidden" style={{ filter: 'drop-shadow(0 0 20px rgba(34,211,238,0.4))', color: '#22d3ee', margin: '0 auto 1rem' }} />
                    <PlayCircle size={64} className="hidden sm:block" style={{ filter: 'drop-shadow(0 0 20px rgba(34,211,238,0.4))', color: '#22d3ee', margin: '0 auto 1rem' }} />
                  </motion.div>
                  <p className="text-slate-400 font-mono text-[10px] sm:text-xs uppercase tracking-widest">Cliquez pour lancer</p>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0">
                <PESASimulatorSpot initialStep={0} autoPlay={true} />
                <button
                  onClick={() => setShowSpot(false)}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 z-50 px-3 py-1.5 bg-black/80 font-mono text-indigo-400 text-xs"
                  style={{ border: '1px solid rgba(34,211,238,0.3)' }}
                >Fermer</button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══ PRICING ══ */}
      <section className="py-12 sm:py-20 px-4 sm:px-6" style={{ borderTop: '1px solid rgba(34,211,238,0.05)', borderBottom: '1px solid rgba(34,211,238,0.05)', background: 'rgba(2,6,23,0.6)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="text-[11px] font-mono text-indigo-200 uppercase tracking-[0.3em] mb-2">Section V</div>
            {/* MOBILE FIX : clamp h2 */}
            <h2 className="font-black uppercase tracking-tight text-white"
              style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
              Tarification{' '}
              <span style={{ background: 'linear-gradient(90deg, #22d3ee, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Academia
              </span>
            </h2>
            <p className="text-slate-100 font-mono text-xs uppercase tracking-wider mt-2">Simple · Transparent · Sans engagement</p>
          </div>

          {/* MOBILE FIX : grid-cols-1 → md:grid-cols-2, border séparateur adapté */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0" style={{ border: '1px solid rgba(34,211,238,0.08)' }}>

            {/* Academia */}
            <div className="flex flex-col p-6 sm:p-8"
              style={{ borderBottom: '1px solid rgba(34,211,238,0.08)' }}
              /* Note : sur md+, ajouter md:border-r et supprimer border-bottom via className si Tailwind JIT actif */
            >
              <div className="text-[11px] font-mono text-indigo-200 uppercase tracking-[0.3em] mb-3">Plan Standard</div>
              <h3 className="text-xl font-black uppercase text-white mb-1">Academia</h3>
              <div className="flex items-baseline gap-2 mb-6 mt-3 flex-wrap">
                <span className="text-4xl sm:text-5xl font-black tracking-tight" style={{ background: 'linear-gradient(90deg, #22d3ee, #67e8f9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>€2 400</span>
                <span className="text-slate-100 font-mono text-sm">/an</span>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {["Jusqu'à 150 étudiants", "Tous les templates disponibles", "Analytics complètes", "Support prioritaire", "Formation 3 enseignants incluse"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-100 text-sm">
                    <CheckCircle size={14} className="text-emerald-500/70 shrink-0" />
                    <span style={{ fontStyle: 'italic' }}>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/auth/register?plan=academia')}
                className="w-full py-3.5 font-black uppercase text-white text-sm tracking-wider transition-all"
                style={{ border: '1px solid rgba(34,211,238,0.25)', background: 'rgba(34,211,238,0.05)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(34,211,238,0.10)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(34,211,238,0.05)'; e.currentTarget.style.borderColor = 'rgba(34,211,238,0.25)'; }}
              >
                Essai 30 j gratuit
              </button>
            </div>

            {/* Academia Pro */}
            <div className="flex flex-col p-6 sm:p-8 relative" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.05), rgba(249,115,22,0.04))' }}>
              <div className="absolute top-4 right-4 px-2.5 py-1 text-[10px] font-black font-mono uppercase tracking-widest text-amber-300"
                style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.2)' }}>
                Grande École
              </div>
              <div className="text-[11px] font-mono text-amber-500/40 uppercase tracking-[0.3em] mb-3">Plan Avancé</div>
              <h3 className="text-xl font-black uppercase text-white mb-1">Academia Pro</h3>
              <div className="flex items-baseline gap-2 mb-6 mt-3 flex-wrap">
                <span className="text-4xl sm:text-5xl font-black tracking-tight" style={{ background: 'linear-gradient(90deg, #f59e0b, #f97316)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>€9 600</span>
                <span className="text-slate-100 font-mono text-sm">/an</span>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {["Jusqu'à 500 étudiants", "Panel Grande École complet", "White-label & personnalisation", "API + intégrations SI", "Support réactif < 24h"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white text-sm">
                    <CheckCircle size={14} className="text-amber-400/70 shrink-0" />
                    <span style={{ fontStyle: 'italic' }}>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/contact?from=education&sujet=demo')}
                className="w-full py-3.5 font-black uppercase text-white text-sm tracking-wider transition-all"
                style={{ border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.07)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.12)'; e.currentTarget.style.borderColor = 'rgba(245,158,11,0.55)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.07)'; e.currentTarget.style.borderColor = 'rgba(245,158,11,0.35)'; }}
              >
                Demander une démo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══ CTA FINAL ══ */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-8 sm:p-14 overflow-hidden" style={{
            background: 'rgba(2,6,23,0.8)',
            border: '1px solid rgba(34,211,238,0.07)',
          }}>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(34,211,238,0.05),transparent_65%)] pointer-events-none" />
            <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-indigo-500/20" />
            <span className="absolute top-0 right-0 w-6 h-6 border-t border-r border-indigo-500/20" />
            <span className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-amber-500/15" />
            <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-amber-500/15" />

            {/* MOBILE FIX : clamp h2 */}
            <h2 className="font-black uppercase tracking-tight text-white mb-4 relative z-10"
              style={{ fontSize: 'clamp(1.3rem, 5vw, 1.875rem)' }}>
              Prêt à transformer vos cours ?
            </h2>
            <p className="text-slate-400 mb-8 sm:mb-10 relative z-10 max-w-md mx-auto text-sm leading-relaxed" style={{ fontStyle: 'italic' }}>
              Essai 30 jours. Sans carte bancaire. 3 enseignants formés inclus.
            </p>

            {/* MOBILE FIX : form flex-col par défaut → sm:flex-row, input full-width mobile */}
            <form
              onSubmit={e => { e.preventDefault(); navigate('/auth/register', { state: { email, plan: 'academia' } }); }}
              className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto relative z-10"
            >
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="votre.email@etablissement.fr"
                required
                className="flex-1 px-4 py-3.5 bg-black/60 text-white placeholder:text-slate-700 focus:outline-none text-sm font-mono transition-all w-full"
                style={{ border: '1px solid rgba(34,211,238,0.08)' }}
                onFocus={e => e.currentTarget.style.borderColor = 'rgba(34,211,238,0.25)'}
                onBlur={e => e.currentTarget.style.borderColor = 'rgba(34,211,238,0.08)'}
              />
              <button
                type="submit"
                className="px-7 py-3.5 font-black uppercase text-white text-sm tracking-wider transition-all whitespace-nowrap w-full sm:w-auto"
                style={{ border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.07)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.07)'; }}
              >
                Démarrer l'essai
              </button>
            </form>
          </div>
        </div>
      </section>

      <footer className="py-8 px-6 text-center text-xs text-white font-mono uppercase tracking-widest"
        style={{ borderTop: '1px solid rgba(34,211,238,0.05)', background: 'rgba(2,6,23,0.5)' }}>
        © 2026 · PESACORE Architect : Tous droits réservés.
      </footer>
    </div>
  );
}